import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { useRef, useEffect, useState } from "react";

interface CounterProps {
  value: string;
  duration?: number;
  className?: string;
}

/**
 * Composant pour les animations de compteur (pour les stats)
 */
export const Counter = ({ value, duration = 2, className = "" }: CounterProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [displayValue, setDisplayValue] = useState("0");

  // Gérer différents formats : "100+", "4.7/5", "95%"
  let numericValue: number;
  let suffix: string;
  let isDecimal = false;

  if (value.includes("/")) {
    // Format "4.7/5"
    const parts = value.split("/");
    numericValue = parseFloat(parts[0]) || 0;
    suffix = "/" + parts[1];
    isDecimal = parts[0].includes(".");
  } else if (value.includes("%")) {
    // Format "95%"
    const cleanValue = value.replace("%", "");
    numericValue = parseFloat(cleanValue) || 0;
    suffix = "%";
    isDecimal = cleanValue.includes(".");
  } else {
    // Format "100+" ou similaire
    const cleanValue = value.replace(/[^\d.]/g, "");
    numericValue = parseFloat(cleanValue) || 0;
    suffix = value.replace(/[\d.]/g, "");
    isDecimal = cleanValue.includes(".");
  }

  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, {
    damping: 60,
    stiffness: 100,
  });

  useEffect(() => {
    if (isInView) {
      motionValue.set(numericValue);
    }
  }, [isInView, numericValue, motionValue]);

  useEffect(() => {
    const unsubscribe = springValue.on("change", (latest) => {
      if (isDecimal) {
        // Pour les décimales, afficher avec 1 décimale
        setDisplayValue(latest.toFixed(1) + suffix);
      } else {
        // Pour les entiers, arrondir
        setDisplayValue(Math.floor(latest) + suffix);
      }
    });
    return unsubscribe;
  }, [springValue, suffix, isDecimal]);

  // Respecter prefers-reduced-motion
  const prefersReducedMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <motion.span
      ref={ref}
      className={className}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
      transition={
        prefersReducedMotion ? { duration: 0 } : { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
      }
    >
      {displayValue}
    </motion.span>
  );
};

// Version simplifiée pour les valeurs avec formatage complexe
export const AnimatedValue = ({
  value,
  className = "",
  delay = 0,
}: {
  value: string;
  className?: string;
  delay?: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.span
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 10 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {value}
    </motion.span>
  );
};
